// =============================================================================
// VulnTracker — Notifications Page
// =============================================================================
import { useState } from 'react';
import { useNotifications, useMarkRead, useMarkAllRead } from '@/api/hooks';
import { Pagination } from '@/components/Pagination';
import toast from 'react-hot-toast';
import { formatDistanceToNow, format } from 'date-fns';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  TicketIcon,
  CheckCircleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import type { Notification } from '@/types';

// ── Channel display helpers ──────────────────────────────────────────────
const CHANNEL_ICON: Record<string, React.ReactElement> = {
  email:  <EnvelopeIcon className="h-3.5 w-3.5" />,
  slack:  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />,
  teams:  <UserGroupIcon className="h-3.5 w-3.5" />,
  jira:   <TicketIcon className="h-3.5 w-3.5" />,
};
const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email', slack: 'Slack', teams: 'Teams', jira: 'Jira',
};

// ── Severity styling ─────────────────────────────────────────────────────
const SEV_BADGE: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border border-red-200',
  high:     'bg-orange-100 text-orange-800 border border-orange-200',
  medium:   'bg-yellow-100 text-yellow-800 border border-yellow-200',
  low:      'bg-blue-100 text-blue-800 border border-blue-200',
  none:     'bg-gray-100 text-gray-600 border border-gray-200',
};
const SEV_BORDER: Record<string, string> = {
  critical: 'border-l-red-500',
  high:     'border-l-orange-500',
  medium:   'border-l-yellow-400',
  low:      'border-l-blue-400',
  none:     'border-l-gray-200',
};

// ── Status styling ───────────────────────────────────────────────────────
const STATUS_BADGE: Record<string, string> = {
  sent:      'bg-green-100 text-green-800',
  delivered: 'bg-green-100 text-green-800',
  read:      'bg-gray-100 text-gray-500',
  pending:   'bg-yellow-100 text-yellow-800',
  retrying:  'bg-yellow-100 text-yellow-800',
  failed:    'bg-red-100 text-red-800',
  bounced:   'bg-red-100 text-red-800',
};

function timeAgo(dateStr: string) {
  try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true }); }
  catch { return dateStr; }
}
function absTime(dateStr: string) {
  try { return format(new Date(dateStr), 'MMM d, yyyy HH:mm'); }
  catch { return dateStr; }
}

export function NotificationsPage() {
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading } = useNotifications({
    page,
    page_size: 20,
    ...(channel && { channel }),
    ...(status && { status }),
  });

  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const handleMarkAllRead = async () => {
    try {
      await markAllRead.mutateAsync();
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await markRead.mutateAsync(id);
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const unreadCount = (data?.items ?? []).filter(
    (n: Notification) => n.status === 'sent' || n.status === 'delivered' || n.status === 'pending',
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="mt-1 text-sm text-gray-500 flex items-center gap-2">
            {data?.total ?? 0} total
            {unreadCount > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <button
          className="btn-secondary flex items-center gap-2"
          onClick={handleMarkAllRead}
          disabled={markAllRead.isPending || unreadCount === 0}
        >
          <CheckCircleIcon className="h-4 w-4" />
          Mark All Read
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-600">
          <FunnelIcon className="h-4 w-4" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
            <select
              className="input-field px-3"
              value={channel}
              onChange={(e) => { setChannel(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              <option value="email">Email</option>
              <option value="slack">Slack</option>
              <option value="teams">Teams</option>
              <option value="jira">Jira</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              className="input-field px-3"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="failed">Failed</option>
              <option value="bounced">Bounced</option>
              <option value="retrying">Retrying</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              className="btn-secondary w-full"
              onClick={() => { setChannel(''); setStatus(''); setPage(1); }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Notification list */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            {(data?.items ?? []).map((notif: Notification) => {
              const sev = (notif.severity ?? 'none').toLowerCase();
              const isUnread = notif.status === 'sent' || notif.status === 'delivered';
              const timeStr = notif.sent_at ?? notif.created_at;

              return (
                <div
                  key={notif.id}
                  className={`card border-l-4 ${SEV_BORDER[sev] ?? SEV_BORDER.none} ${isUnread ? 'bg-white' : 'bg-gray-50/60'} transition-colors`}
                >
                  {/* Row 1: severity · subject · channel · status · time */}
                  <div className="flex flex-wrap items-start gap-2">
                    {notif.severity && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${SEV_BADGE[sev] ?? SEV_BADGE.none}`}>
                        {notif.severity}
                      </span>
                    )}

                    <p className={`flex-1 min-w-0 text-sm font-semibold ${isUnread ? 'text-gray-900' : 'text-gray-500'}`}>
                      {notif.cve_id && (
                        <span className="font-mono mr-2 text-gray-800">{notif.cve_id}</span>
                      )}
                      <span className="font-normal text-gray-500">
                        {/* Strip leading "[SEVERITY] CVE-xxx " from subject since those are shown separately */}
                        {notif.subject.replace(/^\[[A-Z]+\]\s+CVE-[\d-]+\s*/i, '').trim() || notif.subject}
                      </span>
                    </p>

                    {/* Channel pill */}
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                      {CHANNEL_ICON[notif.channel] ?? null}
                      {CHANNEL_LABEL[notif.channel] ?? notif.channel}
                    </span>

                    {/* Status badge */}
                    <span className={`badge text-xs shrink-0 ${STATUS_BADGE[notif.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {notif.status}
                    </span>

                    {/* Relative time */}
                    <span
                      className="text-xs text-gray-400 whitespace-nowrap shrink-0"
                      title={absTime(timeStr)}
                    >
                      {timeAgo(timeStr)}
                    </span>
                  </div>

                  {/* Row 2: sent-to · project · organisation · retries · mark read */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
                    {notif.sent_to && (
                      <span>
                        <span className="font-medium text-gray-600">Sent to:</span>{' '}
                        {notif.sent_to}
                      </span>
                    )}
                    {notif.project && (
                      <span>
                        <span className="font-medium text-gray-600">Project:</span>{' '}
                        {notif.project}
                      </span>
                    )}
                    <span>
                      <span className="font-medium text-gray-600">Organisation:</span>{' '}
                      {notif.organization ?? '—'}
                    </span>
                    {notif.retry_count > 0 && (
                      <span className="text-amber-600 font-medium">{notif.retry_count} retr{notif.retry_count === 1 ? 'y' : 'ies'}</span>
                    )}
                    {isUnread && (
                      <button
                        className="ml-auto text-xs text-primary-600 hover:text-primary-800 font-medium"
                        onClick={() => handleMarkRead(notif.id)}
                        disabled={markRead.isPending}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {(data?.items ?? []).length === 0 && (
              <div className="card text-center text-gray-500 py-12">
                No notifications found
              </div>
            )}
            <Pagination
              page={page}
              totalPages={data?.total_pages ?? 1}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
